import { useState, useMemo, useEffect } from "react";
import { useParams } from "wouter";
import { setStoreTimezone } from "@/lib/store-timezone";
import { Loader2, CalendarDays, Clock, ChevronLeft, ChevronRight, Scissors, MapPin, UserRound, X, Plus, User, CheckCircle2, MessageCircle } from "lucide-react";
import { Link } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch, customerCustomFetch } from "@workspace/api-client-react";
import { formatCurrency } from "@/lib/utils";
import { useStoreColors } from "@/hooks/use-store-colors";
import { useCatalogRealtime } from "@/hooks/use-catalog-realtime";
import { useToast } from "@/hooks/use-toast";
import { errorTitle } from "@/lib/messages";
import { extractApiErrorMessage } from "@/lib/api-error";
import { useCustomerAuth } from "@/hooks/use-customer-auth";
import { ClosedStoreCard } from "@/components/ClosedStoreCard";
import { StoreUnavailable } from "@/components/StoreUnavailable";
import { UnitSelector } from "@/components/UnitSelector";
import { CustomerLoginModal } from "@/components/CustomerLoginModal";
import { BrandTile } from "@/components/BrandTile";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";

interface BookingStore {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  whatsappNumber: string;
  colorPrimary: string;
  colorSecondary: string;
  colorAccent: string;
  appointmentMessageTemplate: string | null;
  appointmentRequestTemplate: string | null;
  address: string | null;
  businessHours: Record<string, { open: string; close: string; enabled: boolean }> | null;
}

interface BookingService {
  id: string;
  name: string;
  description: string | null;
  price: string;
  durationMinutes: number;
  imageUrl: string | null;
  categoryId: string | null;
  status: string;
  category: { id: string; name: string; sortOrder: number } | null;
}

interface BookingProfessional {
  id: string;
  name: string;
  phone: string | null;
  avatarUrl: string | null;
}

interface SlotInfo {
  time: string;
  professionalId: string | null;
  professionalName: string | null;
}

interface PublicUnit {
  id: string;
  name: string;
  address: string | null;
  whatsappNumber: string;
  isParent: boolean;
}

interface BookedItem {
  service: BookingService;
  date: string;
  slot: SlotInfo;
}

function useBookingPage(slug: string, unitId: string | null) {
  // Sino do catálogo: serviços/profissionais/horários editados OU um slot
  // ocupado por outro cliente refletem na página aberta. As queries do
  // /agendar têm chaves próprias, então a invalidação é feita aqui via onPing.
  const bookingQueryClient = useQueryClient();
  useCatalogRealtime(slug, () => {
    void bookingQueryClient.invalidateQueries({ queryKey: ["booking", slug] });
    void bookingQueryClient.invalidateQueries({ queryKey: ["booking-available-dates", slug] });
    void bookingQueryClient.invalidateQueries({ queryKey: ["booking-slots", slug] });
  });

  return useQuery<{ store: BookingStore; services: BookingService[]; professionals: BookingProfessional[]; isOpen: boolean; isBlocked: boolean; paused?: boolean; storeWhatsapp: string; units: PublicUnit[]; selectedUnitId: string }>({
    queryKey: ["booking", slug, unitId],
    queryFn: () => {
      const url = unitId
        ? `/api/booking/${slug}?unitId=${unitId}`
        : `/api/booking/${slug}`;
      return customFetch(url);
    },
    enabled: !!slug,
  });
}

function useAvailableDates(slug: string, serviceId: string, month: number, year: number, unitId: string | null) {
  return useQuery<{ availableDates: string[] }>({
    queryKey: ["booking-available-dates", slug, serviceId, month, year, unitId],
    queryFn: () => {
      let url = `/api/booking/${slug}/available-dates?serviceId=${serviceId}&month=${month}&year=${year}`;
      if (unitId) url += `&unitId=${unitId}`;
      return customFetch(url);
    },
    enabled: !!slug && !!serviceId && !!month && !!year,
  });
}

function useSlots(slug: string, date: string, serviceId: string, unitId: string | null) {
  return useQuery<{ slots: SlotInfo[] }>({
    queryKey: ["booking-slots", slug, date, serviceId, unitId],
    queryFn: () => {
      let url = `/api/booking/${slug}/slots?date=${date}&serviceId=${serviceId}`;
      if (unitId) url += `&unitId=${unitId}`;
      return customFetch(url);
    },
    enabled: !!slug && !!date && !!serviceId,
  });
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

const dayNames = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

function formatDateBR(dateStr: string) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function slotsConflict(existingSlot: SlotInfo, existingDate: string, existingDuration: number, candidateSlot: SlotInfo, candidateDate: string, candidateDuration: number): boolean {
  if (existingDate !== candidateDate) return false;
  if (existingSlot.professionalId !== candidateSlot.professionalId) return false;
  const eStart = timeToMinutes(existingSlot.time);
  const eEnd = eStart + existingDuration;
  const cStart = timeToMinutes(candidateSlot.time);
  const cEnd = cStart + candidateDuration;
  return cStart < eEnd && cEnd > eStart;
}

type Step = "service" | "date" | "time" | "review" | "info" | "confirmado";

export default function Booking() {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const { data, isLoading, error } = useBookingPage(slug || "", selectedUnitId);

  const [step, setStep] = useState<Step>("service");
  const [selectedService, setSelectedService] = useState<BookingService | null>(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState<SlotInfo | null>(null);
  const { customer, isLoading: customerLoading } = useCustomerAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [isBooking, setIsBooking] = useState(false);
  const [bookingBlocked, setBookingBlocked] = useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>("all");

  const [bookedItems, setBookedItems] = useState<BookedItem[]>([]);
  // Depois do agendamento: o que foi agendado e o link do WhatsApp (tela «Agendamento feito!»).
  const [confirmados, setConfirmados] = useState<BookedItem[]>([]);
  const [linkDoWhats, setLinkDoWhats] = useState("");
  const qc = useQueryClient();

  const { data: availableDatesData, isLoading: loadingDates } = useAvailableDates(
    slug || "",
    selectedService?.id || "",
    calendarMonth.getMonth() + 1,
    calendarMonth.getFullYear(),
    selectedUnitId,
  );

  const availableDatesSet = useMemo(() => {
    return new Set(availableDatesData?.availableDates || []);
  }, [availableDatesData]);

  const { data: slotsData, isLoading: loadingSlots } = useSlots(slug || "", selectedDate, selectedService?.id || "", selectedUnitId);

  const filteredSlots = useMemo(() => {
    if (!slotsData?.slots || !selectedService) return [];
    return slotsData.slots.filter(slot => {
      return !bookedItems.some(item =>
        slotsConflict(item.slot, item.date, item.service.durationMinutes, slot, selectedDate, selectedService.durationMinutes)
      );
    });
  }, [slotsData, bookedItems, selectedDate, selectedService]);

  const store = data?.store;
  const services = data?.services || [];

  useStoreColors(store);

  // Relógio da loja pros helpers de data/promoção.
  useEffect(() => {
    setStoreTimezone((store as { timezone?: string | null } | undefined)?.timezone ?? null);
  }, [store]);

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const now = new Date();
    const brParts = now.toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" }).split("-");
    const todayStr = `${brParts[0]}-${brParts[1]}-${brParts[2]}`;

    const days: { date: Date; dateStr: string; disabled: boolean; day: number; available: boolean }[] = [];
    for (let i = 0; i < firstDay; i++) {
      days.push({ date: new Date(0), dateStr: "", disabled: true, day: 0, available: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const isPast = dateStr < todayStr;
      const available = !isPast && availableDatesSet.has(dateStr);
      days.push({ date, dateStr, disabled: isPast || !available, day: d, available });
    }
    return days;
  }, [calendarMonth, availableDatesSet]);

  const allItems = bookedItems;
  const totalPrice = allItems.reduce((sum, item) => sum + Number(item.service.price), 0);

  const handleRemoveItem = (index: number) => {
    setBookedItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddMore = () => {
    setSelectedService(null);
    setSelectedDate("");
    setSelectedSlot(null);
    setStep("service");
  };

  const handleContinueToInfo = () => {
    if (!customer) {
      setLoginOpen(true);
      return;
    }
    setStep("info");
  };

  const handleBook = async () => {
    if (!store || allItems.length === 0) return;
    if (!customer) {
      setLoginOpen(true);
      return;
    }
    setIsBooking(true);

    const itens = allItems;
    const succeededIndexes: number[] = [];
    try {
      for (let i = 0; i < itens.length; i++) {
        const item = itens[i];
        await customerCustomFetch(`/api/booking/${slug}/book`, {
          method: "POST",
          body: JSON.stringify({
            serviceId: item.service.id,
            professionalId: item.slot.professionalId,
            date: item.date,
            time: item.slot.time,
            ...(selectedUnitId ? { unitId: selectedUnitId } : {}),
          }),
        });
        succeededIndexes.push(i);
      }
      // Agendado: a tela vira «Agendamento feito!» e o carrinho zera (o 2º toque dava 409 no próprio horário). O WhatsApp
      // abre na MESMA aba, como no checkout de pedidos: window.open depois do await o Safari do iPhone bloqueia, porque
      // já não conta como toque (RJ LIMPEZA, 19/10/2026). O botão da tela fica para um toque direto.
      const link = linkDoAgendamento(itens);
      setConfirmados(itens);
      setLinkDoWhats(link);
      setBookedItems([]);
      setStep("confirmado");
      window.location.href = link;
    } catch (err) {
      const status = err instanceof Error && "status" in err ? (err as { status: number }).status : undefined;
      if (status === 403) {
        setBookingBlocked(true);
      } else if (status === 401) {
        setLoginOpen(true);
      } else {
        const falhou = itens[succeededIndexes.length];
        const agendados = succeededIndexes.map((i) => itens[i].service.name).join(", ");
        if (succeededIndexes.length > 0) {
          setBookedItems(prev => prev.filter((_, i) => !succeededIndexes.includes(i)));
          toast({
            title: "Agendamento parcial",
            description: status === 409
              ? `Agendado: ${agendados}. O horário de ${falhou.service.name} acabou de ser reservado: escolha outro.`
              : `Agendado: ${agendados}. Não deu para agendar ${falhou.service.name}: tente de novo.`,
          });
        } else if (status === 409) {
          toast({ title: "Esse horário acabou de ser reservado", description: "Escolha outro horário.", variant: "destructive" });
        } else {
          toast({
            title: errorTitle("concluir o agendamento"),
            // Sem resposta do servidor = rede; com resposta, o motivo dele.
            description: status === undefined
              ? "Verifique sua conexão e tente de novo em instantes."
              : extractApiErrorMessage(err, "Tente de novo em instantes."),
            variant: "destructive",
          });
        }
        if (status === 409 && falhou) {
          // Horário tomado: os horários vêm de novo e o serviço volta para escolher outro.
          void qc.invalidateQueries({ queryKey: ["booking-slots", slug] });
          void qc.invalidateQueries({ queryKey: ["booking-available-dates", slug] });
          setBookedItems(prev => prev.filter((it) => it !== falhou));
          setSelectedService(falhou.service);
          setSelectedDate(falhou.date);
          setSelectedSlot(null);
          setStep("time");
        }
      }
    } finally {
      setIsBooking(false);
    }
  };

  const handleAgendarOutro = () => {
    setConfirmados([]);
    setLinkDoWhats("");
    setSelectedService(null);
    setSelectedDate("");
    setSelectedSlot(null);
    setStep("service");
  };

  const linkDoAgendamento = (itens: BookedItem[]): string => {
    if (!store) return "";
    let msg: string;
    if (itens.length === 1) {
      const item = itens[0];
      const template = store.appointmentRequestTemplate ||
        "Olá! Acabei de agendar pelo link 📅\n\n*Cliente:* {cliente_nome}\n*Serviço:* {servico}\n*Data:* {data}\n*Horário:* {hora}\n*Duração:* {duracao}\n*Valor:* {preco}";
      msg = template
        .replace(/\{cliente_nome\}/g, customer?.firstName || "")
        .replace(/\{servico\}/g, item.service.name || "")
        .replace(/\{data\}/g, formatDateBR(item.date))
        .replace(/\{hora\}/g, item.slot.time || "")
        .replace(/\{duracao\}/g, `${item.service.durationMinutes}min`)
        .replace(/\{preco\}/g, formatCurrency(Number(item.service.price)))
        .replace(/\{profissional\}/g, item.slot.professionalName || "")
        .replace(/\{loja_nome\}/g, store.name || "");
    } else {
      msg = `Olá! Acabei de agendar pelo link 📅\n\n*Cliente:* ${customer?.firstName ?? ""}\n`;
      itens.forEach((item, i) => {
        msg += `\n*${i + 1}) ${item.service.name}*\n`;
        msg += `Data: ${formatDateBR(item.date)}\n`;
        msg += `Horário: ${item.slot.time}\n`;
        if (item.slot.professionalName) msg += `Profissional: ${item.slot.professionalName}\n`;
        msg += `Duração: ${item.service.durationMinutes}min\n`;
        msg += `Valor: ${formatCurrency(Number(item.service.price))}\n`;
      });
      msg += `\n*Valor total:* ${formatCurrency(itens.reduce((sum, item) => sum + Number(item.service.price), 0))}`;
    }
    const phone = store.whatsappNumber.replace(/\D/g, "");
    return `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(msg)}`;
  };

  if (isLoading) {
    return <BookingSkeleton />;
  }

  if (error || !store) {
    return (
      <div className="min-h-[100svh] flex items-center justify-center bg-gray-50 p-4">
        <EmptyState
          icon={CalendarDays}
          title="Página não encontrada"
          description="Esta loja não existe ou não oferece agendamento online no momento."
        />
      </div>
    );
  }

  // Loja com "Loja ativa" desligada: antes caía no "Página não encontrada" acima.
  if (data?.paused) {
    return <StoreUnavailable storeName={store.name} logoUrl={store.logoUrl} whatsapp={data.storeWhatsapp || store.whatsappNumber} colorPrimary={store.colorPrimary} colorSecondary={store.colorSecondary} isBooking={true} pausada={true} />;
  }

  if (data?.isBlocked || bookingBlocked) {
    return <StoreUnavailable storeName={store.name} logoUrl={store.logoUrl} whatsapp={data?.storeWhatsapp || store.whatsappNumber} colorPrimary={store.colorPrimary} colorSecondary={store.colorSecondary} isBooking={true} />;
  }

  const units = data?.units || [];
  if (units.length > 1 && !selectedUnitId) {
    return (
      <UnitSelector
        storeName={store.name}
        logoUrl={store.logoUrl}
        units={units}
        colorPrimary={store.colorPrimary}
        colorSecondary={store.colorSecondary}
        colorAccent={store.colorAccent}
        onSelect={(id) => setSelectedUnitId(id)}
      />
    );
  }

  const primary = store.colorPrimary || "#DC2626";
  const secondary = store.colorSecondary || "#FACC15";
  const accent = store.colorAccent || primary;

  const stepOrder: Step[] = ["service", "date", "time", "review", "info"];
  const currentStepIndex = step === "confirmado" ? stepOrder.length : stepOrder.indexOf(step);


  return (
    <div className="min-h-[100svh]" style={{ backgroundColor: `${primary}06` }}>
      <header className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          {store.logoUrl ? (
            <img src={store.logoUrl} alt={store.name} className="w-10 h-10 rounded-xl object-cover" />
          ) : (
            <BrandTile size="sm" className="w-10 h-10 rounded-xl" />
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-gray-900 truncate">{store.name}</h1>
            {store.address && <p className="text-xs text-gray-500 flex items-center gap-1 truncate"><MapPin className="w-3 h-3 flex-shrink-0" />{store.address}</p>}
          </div>
          {customer ? (
            <Link
              href={`/conta?store=${store.slug}`}
              aria-label="Minha conta"
              className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold border border-border/50 shadow-sm hover:bg-primary/20 transition-colors flex-shrink-0"
              style={{ backgroundColor: `${primary}1a`, color: primary }}
            >
              {customer.avatarBase64 ? (
                <img src={customer.avatarBase64.startsWith("data:") ? customer.avatarBase64 : `data:image/jpeg;base64,${customer.avatarBase64}`} alt={customer.firstName} className="w-full h-full rounded-full object-cover" />
              ) : (
                <span>{(customer.firstName || "?").charAt(0).toUpperCase()}</span>
              )}
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => setLoginOpen(true)}
              aria-label="Entrar com WhatsApp"
              className="w-10 h-10 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-gray-200 transition-colors flex-shrink-0"
            >
              <User className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="max-w-lg mx-auto px-4 pb-3 flex gap-1">
          {stepOrder.map((s, i) => (
            <div key={s} className="flex-1 h-1 rounded-full" style={{ backgroundColor: currentStepIndex >= i ? primary : "#e5e7eb" }} />
          ))}
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6">
        {step === "service" && (
          <div className="animate-in slide-in-from-right duration-200">
            {bookedItems.length > 0 && (
              <button onClick={() => setStep("review")} className="flex items-center gap-1 text-sm text-gray-500 mb-4 hover:text-gray-700">
                <ChevronLeft className="w-4 h-4" /> Voltar ao resumo
              </button>
            )}
            <h2 className="text-xl font-bold text-gray-900 mb-1">{bookedItems.length > 0 ? "Adicionar outro serviço" : "Escolha o serviço"}</h2>
            <p className="text-gray-500 text-sm mb-4">Selecione o serviço que deseja agendar.</p>
            {(() => {
              const categories = Array.from(
                new Map(
                  services.filter((s) => s.category).map((s) => [s.category!.id, s.category!])
                ).values()
              );
              const hasCategories = categories.length > 0;
              const filtered = selectedCategoryFilter === "all"
                ? services
                : services.filter((s) => s.categoryId === selectedCategoryFilter);
              const grouped: { categoryName: string | null; services: BookingService[] }[] = [];
              for (const svc of filtered) {
                const catName = svc.category?.name ?? null;
                const existing = grouped.find((g) => g.categoryName === catName);
                if (existing) existing.services.push(svc);
                else grouped.push({ categoryName: catName, services: [svc] });
              }
              const showGroupHeaders = selectedCategoryFilter === "all" && hasCategories;
              return (
                <>
                  {hasCategories && (
                    <div className="flex gap-2 overflow-x-auto mb-4 -mx-4 px-4 scrollbar-none">
                      <button
                        onClick={() => setSelectedCategoryFilter("all")}
                        className="flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-all"
                        style={
                          selectedCategoryFilter === "all"
                            ? { backgroundColor: primary, color: "#fff", borderColor: primary }
                            : { backgroundColor: "#fff", color: "#374151", borderColor: "#e5e7eb" }
                        }
                      >
                        Todos
                      </button>
                      {categories.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategoryFilter(cat.id)}
                          className="flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-all"
                          style={
                            selectedCategoryFilter === cat.id
                              ? { backgroundColor: primary, color: "#fff", borderColor: primary }
                              : { backgroundColor: "#fff", color: "#374151", borderColor: "#e5e7eb" }
                          }
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="space-y-6">
                    {grouped.map((group, gi) => (
                      <div key={group.categoryName ?? `__no_cat_${gi}`}>
                        {showGroupHeaders && group.categoryName && (
                          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3 px-1">
                            {group.categoryName}
                          </p>
                        )}
                        <div className="space-y-3">
                          {group.services.map((svc) => {
                            const isUnavailable = svc.status === "indisponivel";
                            return (
                            <button
                              key={svc.id}
                              onClick={() => { if (!isUnavailable) { setSelectedService(svc); setSelectedDate(""); setSelectedSlot(null); setStep("date"); } }}
                              className={`w-full bg-white rounded-xl border-2 p-4 text-left transition-all ${isUnavailable ? 'opacity-60 grayscale-[0.3] cursor-not-allowed' : 'hover:shadow-md active:scale-[0.99]'}`}
                              style={{ borderColor: selectedService?.id === svc.id && !isUnavailable ? accent : "transparent" }}
                              disabled={isUnavailable}
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ backgroundColor: `${primary}10` }}>
                                  {svc.imageUrl ? <img src={svc.imageUrl} alt={svc.name} className="w-full h-full object-cover" /> : <Scissors className="w-6 h-6" style={{ color: primary }} />}
                                </div>
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900">{svc.name}</p>
                                  {svc.description && <p className="text-sm text-gray-500 mt-0.5">{svc.description}</p>}
                                  <div className="flex items-center gap-3 mt-2">
                                    <span className="font-bold" style={{ color: primary }}>{formatCurrency(Number(svc.price))}</span>
                                    <span className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{svc.durationMinutes}min</span>
                                    {isUnavailable && <span className="text-xs font-bold text-destructive bg-destructive/10 px-2 py-0.5 rounded">Indisponível</span>}
                                  </div>
                                </div>
                              </div>
                            </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {step === "date" && (
          <div className="animate-in slide-in-from-right duration-200">
            <button onClick={() => setStep("service")} className="flex items-center gap-1 text-sm text-gray-500 mb-4 hover:text-gray-700">
              <ChevronLeft className="w-4 h-4" /> Voltar
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Escolha a data</h2>
            <p className="text-gray-500 text-sm mb-6">Apenas datas com disponibilidade estão habilitadas.</p>
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <button aria-label="Mês anterior" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))} className="p-2 hover:bg-gray-100 rounded-lg pointer-coarse:min-h-11 pointer-coarse:min-w-11 flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
                <span className="font-semibold text-gray-900">{monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}</span>
                <button aria-label="Próximo mês" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))} className="p-2 hover:bg-gray-100 rounded-lg pointer-coarse:min-h-11 pointer-coarse:min-w-11 flex items-center justify-center"><ChevronRight className="w-5 h-5" /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((d) => <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((d, i) => (
                  <button
                    key={i}
                    disabled={d.disabled || !d.day || loadingDates}
                    onClick={() => { if (d.dateStr && d.available) { setSelectedDate(d.dateStr); setSelectedSlot(null); setStep("time"); } }}
                    className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                      !d.day ? "invisible" : d.disabled ? "text-gray-200 cursor-not-allowed" : d.dateStr === selectedDate ? "text-white shadow-md" : "text-gray-700 hover:bg-gray-100"
                    }`}
                    style={d.dateStr === selectedDate ? { backgroundColor: primary } : undefined}
                  >
                    {d.day || ""}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === "time" && (
          <div className="animate-in slide-in-from-right duration-200">
            <button onClick={() => setStep("date")} className="flex items-center gap-1 text-sm text-gray-500 mb-4 hover:text-gray-700">
              <ChevronLeft className="w-4 h-4" /> Voltar
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Escolha o horário</h2>
            <p className="text-gray-500 text-sm mb-6">{formatDateBR(selectedDate)} - {selectedService?.name}</p>
            {loadingSlots ? (
              // Skeleton da grade de horários — reserva a silhueta (6 chips
              // pulsando) em vez de spinner cego. Layout shift = zero quando
              // os horários reais chegam.
              <div className="grid grid-cols-3 gap-2 py-4" aria-busy aria-label="Carregando horários disponíveis">
                {Array.from({ length: 9 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 rounded-lg" />
                ))}
              </div>
            ) : filteredSlots.length === 0 ? (
              <EmptyState
                icon={Clock}
                title="Nenhum horário disponível"
                description="Não há horários livres nesta data. Tente outra data ou outro profissional."
              />
            ) : (
              <div className="space-y-2">
                {filteredSlots.map((slot, idx) => {
                  const isSelected = selectedSlot?.time === slot.time && selectedSlot?.professionalId === slot.professionalId;
                  return (
                    <button
                      key={`${slot.time}-${slot.professionalId}-${idx}`}
                      onClick={() => { setSelectedSlot(slot); handleAddCurrentToItemsWithSlot(slot); }}
                      className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-between ${
                        isSelected ? "text-white shadow-md" : "bg-white border text-gray-700 hover:border-gray-300"
                      }`}
                      style={isSelected ? { backgroundColor: primary } : undefined}
                    >
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {slot.time}
                      </span>
                      {slot.professionalName && (
                        <span className="flex items-center gap-1.5 text-xs opacity-80">
                          <UserRound className="w-3.5 h-3.5" />
                          {slot.professionalName}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {step === "review" && (
          <div className="animate-in slide-in-from-right duration-200">
            <h2 className="text-xl font-bold text-gray-900 mb-1">Resumo dos serviços</h2>
            <p className="text-gray-500 text-sm mb-6">
              {bookedItems.length === 1 ? "1 serviço selecionado" : `${bookedItems.length} serviços selecionados`}
            </p>
            <div className="space-y-3">
              {bookedItems.map((item, i) => (
                <div key={i} className="bg-white rounded-xl border p-4 relative">
                  <button
                    aria-label="Remover serviço"
                    onClick={() => handleRemoveItem(i)}
                    className="absolute top-1 right-1 p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-red-500 transition-colors pointer-coarse:p-3 flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${primary}10` }}>
                      {item.service.imageUrl ? <img src={item.service.imageUrl} alt={item.service.name} className="w-full h-full object-cover rounded-lg" /> : <Scissors className="w-5 h-5" style={{ color: primary }} />}
                    </div>
                    <div className="flex-1 min-w-0 pr-6">
                      <p className="font-semibold text-gray-900">{item.service.name}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{formatDateBR(item.date)}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.slot.time}</span>
                        {item.slot.professionalName && <span className="flex items-center gap-1"><UserRound className="w-3 h-3" />{item.slot.professionalName}</span>}
                      </div>
                      <p className="font-bold mt-2" style={{ color: primary }}>{formatCurrency(Number(item.service.price))}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {bookedItems.length > 0 && (
              <div className="mt-4 p-4 bg-white rounded-xl border flex justify-between items-center">
                <span className="font-semibold text-gray-700">Total</span>
                <span className="font-bold text-lg" style={{ color: primary }}>{formatCurrency(totalPrice)}</span>
              </div>
            )}

            <div className="mt-6 space-y-3">
              <button
                onClick={handleAddMore}
                className="w-full py-3 rounded-xl font-semibold text-sm border-2 border-dashed flex items-center justify-center gap-2 transition-all hover:bg-gray-50"
                style={{ borderColor: `${primary}40`, color: primary }}
              >
                <Plus className="w-4 h-4" />
                Adicionar mais um serviço
              </button>
              <button
                onClick={handleContinueToInfo}
                disabled={bookedItems.length === 0 || customerLoading}
                className="w-full py-3.5 rounded-xl font-semibold text-white shadow-lg transition-all active:scale-[0.98] disabled:opacity-50"
                style={{ backgroundColor: primary }}
              >
                Continuar para finalizar
              </button>
            </div>
          </div>
        )}

        {step === "info" && (
          <div className="animate-in slide-in-from-right duration-200">
            <button onClick={() => setStep("review")} className="flex items-center gap-1 text-sm text-gray-500 mb-4 hover:text-gray-700">
              <ChevronLeft className="w-4 h-4" /> Voltar
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Confirme o agendamento</h2>
            <p className="text-gray-500 text-sm mb-6">
              {customer
                ? `Você está agendando como ${customer.firstName}.`
                : "Entre com seu WhatsApp para confirmar."}
            </p>
            <div className="mt-6 space-y-3">
              {allItems.map((item, i) => (
                <div key={i} className="p-4 bg-white rounded-xl border space-y-2">
                  {allItems.length > 1 && <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Serviço {i + 1}</p>}
                  <div className="flex justify-between"><span className="text-gray-500">Serviço</span><span className="font-semibold">{item.service.name}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Data</span><span className="font-semibold">{formatDateBR(item.date)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Horário</span><span className="font-semibold">{item.slot.time}</span></div>
                  {item.slot.professionalName && (
                    <div className="flex justify-between"><span className="text-gray-500">Profissional</span><span className="font-semibold">{item.slot.professionalName}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-gray-500">Duração</span><span className="font-semibold">{item.service.durationMinutes}min</span></div>
                  <div className="flex justify-between border-t pt-2 mt-2"><span className="text-gray-500">Valor</span><span className="font-bold" style={{ color: primary }}>{formatCurrency(Number(item.service.price))}</span></div>
                </div>
              ))}
              {allItems.length > 1 && (
                <div className="p-4 bg-white rounded-xl border flex justify-between">
                  <span className="font-semibold text-gray-700">Total</span>
                  <span className="font-bold text-lg" style={{ color: primary }}>{formatCurrency(totalPrice)}</span>
                </div>
              )}
            </div>
            {data && !data.isOpen && (
              <div className="mt-4">
                <ClosedStoreCard businessHours={data.store.businessHours} type="agendamento" />
              </div>
            )}
            <button
              onClick={handleBook}
              disabled={isBooking || !customer}
              className="w-full mt-6 py-3.5 rounded-xl font-semibold text-white shadow-lg transition-all active:scale-[0.98] disabled:opacity-50"
              style={{ backgroundColor: primary }}
            >
              {isBooking ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : `Confirmar Agendamento${allItems.length > 1 ? "s" : ""}`}
            </button>
            {!customer && (
              <button
                onClick={() => setLoginOpen(true)}
                className="w-full mt-3 py-3 rounded-xl font-semibold border-2 transition-all active:scale-[0.98]"
                style={{ borderColor: primary, color: primary }}
              >
                Entrar com WhatsApp
              </button>
            )}
          </div>
        )}

        {step === "confirmado" && (
          <div className="animate-in fade-in duration-200" data-testid="agendamento-feito">
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: primary }} />
              <h2 className="text-xl font-bold text-gray-900 mt-3">Agendamento feito!</h2>
              <p className="text-gray-500 text-sm mt-1">Mande a confirmação para a loja pelo WhatsApp.</p>
            </div>
            <div className="mt-6 space-y-3">
              {confirmados.map((item, i) => (
                <div key={i} className="p-4 bg-white rounded-xl border">
                  <p className="font-semibold text-gray-900">{item.service.name}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{formatDateBR(item.date)}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.slot.time}</span>
                    {item.slot.professionalName && <span className="flex items-center gap-1"><UserRound className="w-3 h-3" />{item.slot.professionalName}</span>}
                  </div>
                </div>
              ))}
            </div>
            {/* Toque direto: abre mesmo onde a abertura automática não abriu. */}
            <a
              href={linkDoWhats}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full mt-6 py-3.5 rounded-xl font-semibold text-white shadow-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
              style={{ backgroundColor: "#25D366" }}
              data-testid="agendamento-whatsapp"
            >
              <MessageCircle className="w-5 h-5" /> Enviar no WhatsApp
            </a>
            <button
              onClick={handleAgendarOutro}
              className="w-full mt-3 py-3 rounded-xl font-semibold border-2 transition-all active:scale-[0.98]"
              style={{ borderColor: primary, color: primary }}
              data-testid="agendamento-outro"
            >
              Agendar outro
            </button>
          </div>
        )}
      </div>
      <CustomerLoginModal
        open={loginOpen}
        onOpenChange={setLoginOpen}
        onAuthenticated={() => { setStep("info"); }}
        askBirthDate={(store as any)?.askBirthDate}
        birthDateRequired={(store as any)?.birthDateRequired}
      />
    </div>
  );

  function handleAddCurrentToItemsWithSlot(slot: SlotInfo) {
    if (!selectedService || !selectedDate) return;
    setBookedItems(prev => [...prev, { service: selectedService!, date: selectedDate, slot }]);
    setSelectedService(null);
    setSelectedDate("");
    setSelectedSlot(null);
    setStep("review");
  }
}

// Skeleton da landing de agendamento — espelha a silhueta real: header
// (avatar + nome da loja), tira de filtros e ~3 cards de serviço. Substitui
// o spinner de tela cheia anterior (Loader2 sem contexto). Mesmo padrão
// que o storefront já usa (CatalogSkeleton).
function BookingSkeleton() {
  return (
    <div className="min-h-svh bg-gray-50 max-w-lg mx-auto" aria-busy aria-label="Carregando página de agendamento">
      <div className="h-16 bg-white border-b flex items-center px-4 gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="w-32 h-4" />
          <Skeleton className="w-24 h-3" />
        </div>
      </div>
      <div className="p-4 flex gap-2 overflow-hidden">
        <Skeleton className="w-20 h-8 rounded-full" />
        <Skeleton className="w-24 h-8 rounded-full" />
        <Skeleton className="w-16 h-8 rounded-full" />
      </div>
      <div className="px-4 space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="w-full h-24 rounded-xl" />
        ))}
      </div>
    </div>
  );
}
